import type { DepartureTiming, RefundQuoteTicket } from './types'

export interface ResolvePenaltyContext {
  timing: DepartureTiming
  bookingCurrency: string
  /** Fare→booking conversion rate; undefined when unknown (foreign amounts unusable). */
  conversionRate?: number
  cat16CancellationPenalty?: number | null
}

/**
 * Single source of truth for a ticket's cancellation penalty in booking currency.
 *
 * Precedence:
 *   1. Sabre contracted refundFee, when already in booking currency. Foreign-currency
 *      refundFee falls through — refundPenalties handles FX conversion.
 *   2. refundPenalties entry for the timing, FX-converted. "conditionsApply with a
 *      0 penalty" means the penalty is unknown, not free — use Cat 16 instead.
 *   3. The Cat 16 fare-rule penalty.
 *
 * GDS figures are capped at the Cat 16 penalty — Sabre sometimes quotes the higher OR-block
 * tier, or charges per fare component when the rule says per ticket. Null when none exists.
 */
export function resolveTicketPenalty(t: RefundQuoteTicket, ctx: ResolvePenaltyContext): number | null {
  const cat16 = ctx.cat16CancellationPenalty ?? null

  if (t.refundFee?.currencyCode === ctx.bookingCurrency) {
    return cap(parseFloat(t.refundFee.amount) || 0, cat16)
  }

  const pen = t.refundPenalties?.find(rp => rp.applicability === ctx.timing)
  if (pen?.penalty) {
    const raw = parseFloat(pen.penalty.amount) || 0
    if (raw === 0 && pen.conditionsApply && cat16 != null) return cat16
    if (pen.penalty.currencyCode === ctx.bookingCurrency) return cap(raw, cat16)
    if (ctx.conversionRate != null) return cap(Math.round(raw * ctx.conversionRate), cat16)
  }

  return cat16
}

function cap(penalty: number, cat16: number | null): number {
  return cat16 != null && cat16 < penalty ? cat16 : penalty
}

/** True when any GDS penalty on the ticket is filed in a currency other than the booking's. */
export function hasForeignCurrencyPenalty(t: RefundQuoteTicket, bookingCurrency: string): boolean {
  return !!t.refundPenalties?.some(rp => rp.penalty != null && rp.penalty.currencyCode !== bookingCurrency)
}
